import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import HistoryIcon from "@mui/icons-material/History";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";

import Header from "../../components/Header";
import { tokens } from "../../theme";
import { getSwapHistory } from "../../services/scheduling.services";
import jsPDF from "jspdf";

const defaultPaginationModel = { page: 0, pageSize: 10 };

const SwapHistoryView = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const logoUrl = useMemo(() => `${process.env.PUBLIC_URL}/assets/dracenaC.png`, []);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState(defaultPaginationModel);
  const [loading, setLoading] = useState(false);
  const [statusActiveInput, setStatusActiveInput] = useState("");
  const [statusActiveFilter, setStatusActiveFilter] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [previewGenerating, setPreviewGenerating] = useState(false);

  const formatDateTime = useCallback((value, { includeTime = true } = {}) => {
    if (!value) {
      return "-";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return includeTime ? parsed.toLocaleString() : parsed.toLocaleDateString();
  }, []);

  const normalizeEvents = useCallback((rawEvents) => {
    return rawEvents.map((event, index) => {
      const rowId = event?.id ?? `${event?.usuarioA ?? "swap"}-${event?.fechainicio ?? index}`;
      return {
        ...event,
        __rowId: rowId,
      };
    });
  }, []);

  const fetchSwapHistory = useCallback(
    async ({ page, pageSize, statusActive }) => {
      setLoading(true);
      try {
        const response = await getSwapHistory({
          statusActive:
            statusActive !== undefined && statusActive !== null && statusActive !== ""
              ? statusActive
              : undefined,
          limit: pageSize,
          offset: page * pageSize,
        });

        const rawEvents = Array.isArray(response)
          ? response
          : response?.items ||
            response?.data?.items ||
            response?.events ||
            response?.results ||
            response?.rows ||
            response?.data ||
            [];

        const total =
          (Array.isArray(response)
            ? response.length
            : response?.total ??
              response?.data?.total ??
              response?.meta?.total ??
              response?.pagination?.total ??
              response?.count) ?? rawEvents.length;

        const normalized = normalizeEvents(rawEvents);

        setRows(normalized);
        setRowCount(total);
        setSelectedEvent((previous) => {
          if (!normalized.length) {
            return null;
          }
          if (!previous) {
            return normalized[0];
          }
          const stillExists = normalized.some((item) => item.__rowId === previous.__rowId);
          return stillExists ? previous : normalized[0];
        });
      } catch (error) {
        console.error("Error al obtener el historial de intercambios:", error);
        toast.error(error.message || "No se pudo cargar el historial de intercambios");
        setRows([]);
        setRowCount(0);
        setSelectedEvent(null);
      } finally {
        setLoading(false);
      }
    },
    [normalizeEvents]
  );

  useEffect(() => {
    fetchSwapHistory({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      statusActive: statusActiveFilter,
    });
  }, [fetchSwapHistory, paginationModel.page, paginationModel.pageSize, statusActiveFilter, refreshToken]);

  const handlePaginationChange = useCallback((newModel) => {
    setPaginationModel(newModel);
  }, []);

  const handleApplyFilter = useCallback(() => {
    setPaginationModel((previous) => ({ ...previous, page: 0 }));
    setStatusActiveFilter(statusActiveInput.trim());
  }, [statusActiveInput]);

  const handleClearFilter = useCallback(() => {
    setStatusActiveInput("");
    setStatusActiveFilter("");
    setPaginationModel((previous) => ({ ...previous, page: 0 }));
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshToken((previous) => previous + 1);
  }, []);

  const generatePreview = useCallback(
    async (eventData) => {
      if (previewGenerating) {
        return;
      }

      if (!eventData) {
        toast.error("Selecciona un registro para generar la vista previa");
        return;
      }

      if (!eventData.fechainicio) {
        toast.error("El registro no tiene una fecha de inicio válida");
        return;
      }

      const startDate = new Date(eventData.fechainicio);
      if (Number.isNaN(startDate.getTime())) {
        toast.error("La fecha de inicio del registro no es válida");
        return;
      }

      const requestDate = eventData.create_at ? new Date(eventData.create_at) : null;
      const requestDateFormatted =
        requestDate && !Number.isNaN(requestDate.getTime())
          ? requestDate.toLocaleDateString()
          : new Date().toLocaleDateString();
      const formattedStart = startDate.toLocaleDateString();

      const userAName = eventData.nombreUser1?.trim?.()
        ? eventData.nombreUser1
        : String(eventData.usuarioA ?? "-");
      const userBName = eventData.nombreUser2?.trim?.()
        ? eventData.nombreUser2
        : String(eventData.usuarioB ?? "-");

      const reasonText = (eventData.motivo ?? "").trim() || "Sin motivo especificado";

      const toDataUrl = async (url) => {
        if (!url) {
          return null;
        }
        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Status ${response.status}`);
          }
          const blob = await response.blob();
          return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => reject(new Error("No se pudo leer el logo"));
            reader.readAsDataURL(blob);
          });
        } catch (imageError) {
          console.warn("No se pudo cargar el logo para el PDF", imageError);
          return null;
        }
      };

      setPreviewGenerating(true);
      try {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const marginX = 20;
        let currentY = 24;

        const logoDataUrl = await toDataUrl(logoUrl);
        if (logoDataUrl) {
          const logoWidth = 170;
          const logoHeight = 50;
          doc.addImage(logoDataUrl, "PNG", (pageWidth - logoWidth) / 2, currentY - 18, logoWidth, logoHeight);
          currentY += 40;
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.text("SOLICITUD DE CAMBIO DE TURNO", pageWidth / 2, currentY, { align: "center" });
        currentY += 16;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.text(`Fecha de solicitud de permiso: ${requestDateFormatted}`, marginX, currentY);
        currentY += 10;

        doc.text(`Funcionario: ${userAName}`, marginX, currentY);
        currentY += 10;

        doc.text(`Solicito cambio de turno el día: ${formattedStart}`, marginX, currentY);
        currentY += 10;

        doc.text(`Con el funcionario/a: ${userBName}`, marginX, currentY);
        currentY += 10;

        const reasonLines = doc.splitTextToSize(
          `Motivo del cambio de turno: ${reasonText}`,
          pageWidth - marginX * 2
        );
        doc.text(reasonLines, marginX, currentY);
        currentY += reasonLines.length * 8 + 6;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        const commitmentText =
          "Ambos funcionarios aceptamos este cambio de turno y nos comprometemos a cumplir con nuestras responsabilidades en los turnos acordados.";
        const commitmentLines = doc.splitTextToSize(commitmentText, pageWidth - marginX * 2);
        doc.text(commitmentLines, pageWidth / 2, currentY, { align: "center" });
        currentY += commitmentLines.length * 8 + 12;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.text(`Firma del solicitante: __________________________`, marginX, currentY);
        currentY += 10;

        doc.text(`Firma del compañero/a: ________________________`, marginX, currentY);
        currentY += 10;

        doc.text("Firma del representante: _______________________ Fecha: __/__/____", marginX, currentY);

        const pdfUrl = doc.output("bloburl");
        window.open(pdfUrl, "_blank", "noopener,noreferrer");
      } catch (previewError) {
        console.error("Error al generar la vista previa del intercambio:", previewError);
        toast.error("No se pudo generar la vista previa en PDF");
      } finally {
        setPreviewGenerating(false);
      }
    },
    [logoUrl, previewGenerating]
  );

  const handlePreviewSelected = useCallback(() => {
    if (!selectedEvent) {
      toast.error("Selecciona un registro para generar la vista previa");
      return;
    }
    generatePreview(selectedEvent);
  }, [generatePreview, selectedEvent]);

  const handlePreviewFromRow = useCallback(
    (row) => {
      generatePreview(row);
    },
    [generatePreview]
  );

  const handleDeleteFromRow = useCallback(
    (row) => {
      if (!row) {
        toast.error("No se encontró el registro a eliminar");
        return;
      }

      // TODO: Integrar con el endpoint de eliminación cuando esté disponible.
      console.info("Solicitud de eliminación de intercambio", row);
      toast.info("Funcionalidad de eliminación próximamente disponible");
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        minWidth: 120,
        flex: 0.6,
      },
      {
        field: "nombreUser1",
        headerName: "Funcionario A",
        minWidth: 200,
        flex: 1.1,
        renderCell: (params) => (
          <Typography variant="body2" noWrap>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "nombreUser2",
        headerName: "Funcionario B",
        minWidth: 200,
        flex: 1.1,
        renderCell: (params) => (
          <Typography variant="body2" noWrap>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "motivo",
        headerName: "Motivo",
        minWidth: 240,
        flex: 1.4,
        renderCell: (params) => (
          <Typography variant="body2" noWrap>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "fechainicio",
        headerName: "Inicio",
        minWidth: 180,
        flex: 0.9,
        valueGetter: (params) => formatDateTime(params.row.fechainicio),
      },
      {
        field: "fechafin",
        headerName: "Fin",
        minWidth: 180,
        flex: 0.9,
        valueGetter: (params) => formatDateTime(params.row.fechafin),
      },
      {
        field: "status_active",
        headerName: "Estado",
        minWidth: 120,
        flex: 0.6,
        renderCell: (params) => {
          const value = params.value;
          if (value === null || value === undefined || value === "") {
            return "-";
          }
          const isActive = value === true || value === 1 || value === "1";
          return isActive ? "Activo" : "Inactivo";
        },
      },
      {
        field: "id_usuario",
        headerName: "Registrado por",
        minWidth: 140,
        flex: 0.7,
      },
      {
        field: "create_at",
        headerName: "Creado",
        minWidth: 180,
        flex: 0.6,
        valueGetter: (params) => formatDateTime(params.row.create_at),
      },
    //   {
    //     field: "update_at",
    //     headerName: "Actualizado",
    //     minWidth: 180,
    //     flex: 0.6,
    //     valueGetter: (params) => formatDateTime(params.row.update_at),
    //   },
    //   {
    //     field: "delete_at",
    //     headerName: "Eliminado",
    //     minWidth: 180,
    //     flex: 0.9,
    //     valueGetter: (params) => formatDateTime(params.row.delete_at),
    //   },
      {
        field: "actions",
        headerName: "Acciones",
        sortable: false,
        filterable: false,
        minWidth: 140,
        flex: 0.8,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Vista previa PDF" placement="top">
              <span>
                <IconButton
                  size="small"
                  disabled={previewGenerating}
                  onClick={(event) => {
                    event.stopPropagation();
                    handlePreviewFromRow(params.row);
                  }}
                >
                  <Box
                    component="img"
                    src="https://cdn-icons-png.flaticon.com/512/337/337946.png"
                    alt="Vista previa PDF"
                    sx={{ width: 22, height: 22 }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Eliminar" placement="top">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteFromRow(params.row);
                  }}
                  sx={{ width: 26, height: 26 }}

                >
                  <DeleteIcon sx={{ fontSize: 26, marginTop: "8px" }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [formatDateTime, handleDeleteFromRow, handlePreviewFromRow, previewGenerating]
  );

  const detailPayload = useMemo(() => {
    if (!selectedEvent) {
      return {};
    }
    const { __rowId, ...rest } = selectedEvent;
    return rest;
  }, [selectedEvent]);

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Header title="Historial de intercambios" subtitle="Eventos registrados de permisos e intercambios" />
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            label="Estado (status_active)"
            size="small"
            value={statusActiveInput}
            onChange={(event) => setStatusActiveInput(event.target.value)}
            placeholder="1 | 0"
          />
          <Button variant="contained" startIcon={<FilterAltIcon />} onClick={handleApplyFilter}>
            Aplicar
          </Button>
          <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearFilter}>
            Limpiar
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            Actualizar
          </Button>
        </Stack>
      </Box>

      <Box mt={3} height="60vh">
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.__rowId}
          loading={loading}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          onRowClick={(params) => setSelectedEvent(params.row)}
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[900],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[900],
            },
          }}
        />
      </Box>

      <Box mt={3}>
        <Paper elevation={3} sx={{ p: 3, backgroundColor: colors.primary[400] }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            mb={2}
            flexWrap="wrap"
            rowGap={1}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <HistoryIcon />
              <Typography variant="h6">Detalle del evento seleccionado</Typography>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePreviewSelected}
              disabled={!selectedEvent || previewGenerating}
            >
              Vista previa
            </Button>
          </Stack>
          {selectedEvent ? (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                <strong>ID:</strong> {selectedEvent.id ?? selectedEvent.__rowId}
              </Typography>
              <Typography variant="body2">
                <strong>Usuario A:</strong> {selectedEvent.usuarioA || "-"}
                {selectedEvent.nombreUser1 ? ` · ${selectedEvent.nombreUser1}` : ""}
              </Typography>
              <Typography variant="body2">
                <strong>Usuario B:</strong> {selectedEvent.usuarioB || "-"}
                {selectedEvent.nombreUser2 ? ` · ${selectedEvent.nombreUser2}` : ""}
              </Typography>
              <Typography variant="body2">
                <strong>Motivo:</strong> {selectedEvent.motivo || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Inicio:</strong> {formatDateTime(selectedEvent.fechainicio)}
              </Typography>
              <Typography variant="body2">
                <strong>Fin:</strong> {formatDateTime(selectedEvent.fechafin)}
              </Typography>
              <Typography variant="body2">
                <strong>Status activo:</strong> {
                  selectedEvent.status_active === null ||
                  selectedEvent.status_active === undefined ||
                  selectedEvent.status_active === ""
                    ? "-"
                    : selectedEvent.status_active === true ||
                      selectedEvent.status_active === 1 ||
                      selectedEvent.status_active === "1"
                    ? "Activo"
                    : "Inactivo"
                }
              </Typography>
              <Typography variant="body2">
                <strong>Registrado por:</strong> {selectedEvent.id_usuario || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Creado:</strong> {formatDateTime(selectedEvent.create_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Actualizado:</strong> {formatDateTime(selectedEvent.update_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Eliminado:</strong> {formatDateTime(selectedEvent.delete_at)}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Datos del registro</Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 240, overflow: "auto" }}>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: 12 }}
                >
                  {Object.keys(detailPayload).length ? JSON.stringify(detailPayload, null, 2) : "Sin metadata"}
                </Typography>
              </Paper>
            </Stack>
          ) : (
            <Typography variant="body2">Selecciona un registro para ver el detalle.</Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default SwapHistoryView;
